import axios from 'axios';
import { orderBy, sortBy, without } from 'lodash';
import { getField, updateField } from 'vuex-map-fields';
import type {
  IAppUser} from '@license-manager/types';
import {
  EAppRoles,
  EManagementRoles,
  EOfflineRevalidationTime,
  IAppUserJwtContent,
  ICustomer,
} from '@license-manager/types';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { Get } from '@nestjs/common';
import { Getter } from 'vuex-class';
import user from './user';
import $http from '../services/api';

export type GetAppUsersParams = Partial<{
  take: number;
  skip: number;
  like: string;
  appRoleId: number;
  customerId: number;
  validity: boolean;
}>;
@Module({ namespaced: true, name: 'licenses' })
export default class Licenses extends VuexModule {
  appusersAndCount: [IAppUser[], number] = [[], 0];
  isLoading = false;
  editor = {
    showSuccess: false,
    showError: false,
  };

  editUser: IAppUser = {
    customeraccess: [],
  };

  sort = {
    field: 'sub',
    asc: true,
  };

  @Mutation
  triggerLoading(loadingState = false) {
    this.isLoading = loadingState;
  }

  @Mutation
  setUsers(users: [IAppUser[], number] = [[], 0]) {
    this.appusersAndCount = users;
  }

  @Mutation
  updateField(options: { path: string; value: unknown }) {
    return updateField(this, options);
  }

  @Mutation
  setSort(payload: { field; asc }) {
    this.sort.field = payload.field;
    this.sort.asc = payload.asc;
  }

  @Mutation
  setEditUser(
    payload: { user: IAppUser; resetEditor: boolean } = {
      user: null,
      resetEditor: true,
    }
  ) {
    this.editUser = { ...payload.user, customeraccess: payload.user?.customeraccess ?? [] };
    if (payload.resetEditor) {
      this.editor.showSuccess = false;
      this.editor.showError = false;
    }
  }

  @Mutation
  setGrantRevoke(payload: { customeraccessIdx; field; granted; revoked }) {
    if (payload.granted) {
      this.editUser.customeraccess[payload.customeraccessIdx].revokes = without(
        this.editUser.customeraccess[payload.customeraccessIdx].revokes,
        payload.field
      );
      if (
        this.editUser.customeraccess[payload.customeraccessIdx].grants.indexOf(
          payload.field
        ) < 0
      ) {
        this.editUser.customeraccess[payload.customeraccessIdx].grants = [
          ...this.editUser.customeraccess[payload.customeraccessIdx].grants,
          payload.field,
        ];
      }
    } else if (payload.revoked) {
      this.editUser.customeraccess[payload.customeraccessIdx].grants = without(
        this.editUser.customeraccess[payload.customeraccessIdx].grants,
        payload.field
      );
      if (
        this.editUser.customeraccess[payload.customeraccessIdx].revokes.indexOf(
          payload.field
        ) < 0
      ) {
        this.editUser.customeraccess[payload.customeraccessIdx].revokes = [
          ...this.editUser.customeraccess[payload.customeraccessIdx].revokes,
          payload.field,
        ];
      }
    } else {
      this.editUser.customeraccess[payload.customeraccessIdx].revokes = without(
        this.editUser.customeraccess[payload.customeraccessIdx].revokes,
        payload.field
      );
      this.editUser.customeraccess[payload.customeraccessIdx].grants = without(
        this.editUser.customeraccess[payload.customeraccessIdx].grants,
        payload.field
      );
    }
  }

  @Mutation
  addcustomeraccess({
    customerId,
    userId,
  }: {
    customerId?: number;
    userId?: number;
  }) {
    this.editUser.customeraccess.push({
      userId: userId ?? null,
      customerId: customerId ?? null,
      appRoleId: 1, // basic role
      offlineRevalidationTime: EOfflineRevalidationTime['30D'],
      expiresAt: null,
      company: null,
      grants: [],
      revokes: [],
      appCustomerPermissions: [],
    });
  }

  @Mutation
  removecustomeraccess({ index }) {
    this.editUser.customeraccess.splice(index, 1);
  }

  @Mutation
  setEditorAfterSubmit(wasSuccessful) {
    if (wasSuccessful) {
      this.editor.showSuccess = true;
      this.editor.showError = false;
    } else {
      this.editor.showSuccess = false;
      this.editor.showError = true;
    }
  }

  @Action
  async getAppusers(params: GetAppUsersParams = {}): Promise<void> {
    this.context.commit('triggerLoading', true);

    await $http
      .get(`/appusers`, {
        params,
      })
      .then((response) => {
        this.context.commit('setUsers', response.data);
      })
      .catch(() => this.context.commit('setUsers'))
      .then(() => this.context.commit('triggerLoading', false));
  }

  @Action
  async getAppuserById(id: string) {
    this.context.commit('triggerLoading', true);
    try {
      const response = await $http.get<IAppUser>(`/appusers/${id}`);
      return response.data;
    } finally {
      this.context.commit('triggerLoading', false);
    }
  }

  @Action({ rawError: true })
  async createEditUser(userId = null) {
    let editUser: IAppUser = {
      username: null,
      password: null,
      firstname: null,
      lastname: null,
      email: null,
      customeraccess: [
        {
          customerId: null,
          customer: undefined,
          appRoleId: undefined,
          offlineRevalidationTime: EOfflineRevalidationTime['30D'],
          expiresAt: null,
          company: null,
          grants: [],
          revokes: [],
          appCustomerPermissions: [],
        },
      ],
    };

    if (userId) {
      const responseUser: IAppUser = await this.context.dispatch(
        'getAppuserById',
        userId
      );
      editUser = {
        id: responseUser.id,
        username: responseUser.username,
        password: null,
        firstname: responseUser.firstname,
        lastname: responseUser.lastname,
        email: responseUser.email,
        customeraccess: responseUser.customeraccess,
        userIsConfirmed: responseUser.userIsConfirmed,
        entraTenantId: responseUser.entraTenantId,
        ssoType: responseUser.ssoType,
      };
    }
    this.context.commit('setEditUser', { user: editUser, resetEditor: true });
  }

  @Action
  async submitEditedAppuser({
    sendUserDataEmail = false,
    attachExisting,
  }: {
    sendUserDataEmail: boolean;
    attachExisting?: boolean;
  }) {
    return new Promise((res, rej) => {
      this.context.commit('triggerLoading', true);

      let submitLicense = { ...this.editUser };

      const id = submitLicense.id ?? null;
      if (!id && typeof attachExisting === 'boolean' &&
        [EManagementRoles.CUSTOMER_ADMIN, EManagementRoles.CUSTOMER_USER].includes(this.context.rootState.user?.user?.role)) {
        submitLicense = attachExisting ? {
          email: this.editUser.email,
          username: this.editUser.username,
          customeraccess: this.editUser.customeraccess,
        } as IAppUser : submitLicense;
        submitLicense['attachExisting'] = attachExisting;
        if (attachExisting) sendUserDataEmail = false;
      }

      let req;

      if (id) {
        req = $http.put(`/appusers/${id}`, submitLicense, {
          params: { sendUserDataEmail: sendUserDataEmail },
        });
      } else {
        req = $http.post(`/appusers`, submitLicense, {
          params: { sendUserDataEmail: sendUserDataEmail },
        });
      }

      req
        .then((response) => {
          this.context.commit('setEditorAfterSubmit', true);
          res(response.data);
        })
        .catch(() => {
          this.context.commit('setEditorAfterSubmit', false);
          rej();
        })
        .then(() => {
          this.context.commit('triggerLoading', false);
          this.context.dispatch('getAppusers');
        });
    });
  }

  @Action
  async checkAttachmentEligibility(payload: { email?: string; username?: string }) {
    const response = await $http.get('appusers/attachment-eligibility', { params: payload });
    if (!['new', 'existing', 'unavailable'].includes(response.data?.status)) {
      throw new Error('Invalid attachment eligibility response');
    }
    const { status, email, username } = response.data;
    if (status === 'existing' &&
      ((email != null && typeof email !== 'string') ||
        (username != null && typeof username !== 'string'))) {
      throw new Error('Invalid attachment eligibility response');
    }
    return { status: status as 'new' | 'existing' | 'unavailable', email, username };
  }

  @Action
  async checkUsernameIsTaken(payload: { username: string; userId: number; creating?: boolean }) {
    if (payload.creating && !payload.userId &&
      [EManagementRoles.CUSTOMER_ADMIN, EManagementRoles.CUSTOMER_USER].includes(this.context.rootState.user?.user?.role)) {
      return false;
    }
    const params = {};
    if (payload.userId) {
      params['userId'] = payload.userId;
    }
    return $http
      .get('appusers/usernameused/' + payload.username, { params })
      .then((res) => res.data)
      .then((res) => res.isUsed);
  }

  @Action
  async checkEmailIsTaken(payload: { email: string; userId: number; creating?: boolean }) {
    if (payload.creating && !payload.userId &&
      [EManagementRoles.CUSTOMER_ADMIN, EManagementRoles.CUSTOMER_USER].includes(this.context.rootState.user?.user?.role)) {
      return false;
    }
    const params = {};
    if (payload.userId) {
      params['userId'] = payload.userId;
    }
    return $http
      .get('appusers/emailused/' + payload.email, { params })
      .then((res) => res.data)
      .then((res) => res.isUsed);
  }

  @Action
  async deleteAppuser(userid = null) {
    return new Promise<void>((res, rej) => {
      this.context.commit('triggerLoading', true);

      $http
        .delete(`/appusers/${userid}`)
        .then(() => res())
        .catch(() => rej())
        .then(() => {
          this.context.commit('triggerLoading', false);
          this.context.dispatch('getAppusers');
        });
    });
  }

  @Action
  async setNewPassword({
    token,
    newpassword,
  }: {
    token: string;
    newpassword: string;
  }) {
    return $http.post('/licenses/password/setnewpassword', {
      token,
      newpassword,
    });
  }

  @Action
  async confirmRegistrationManual(
    userId: number,
    reloadEditUser = true
  ): Promise<IAppUser> {
    if (!userId) {
      throw new Error('User ID is required to confirm registration');
    }

    this.context.commit('triggerLoading', true);


    return $http
      .post('/appusers/registration/confirmRegistration', { userId })
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to confirm registration');
      }).finally(async () => {

        if (reloadEditUser && this.editUser) {
          //await this.context.dispatch('createEditUser',  userId );
          this.context.commit('setEditUser', { user: { ...this.editUser, userIsConfirmed: true }, resetEditor: false });
        }

        this.context.commit('triggerLoading', false);

      });
  }

  get appusers() {
    return this.appusersAndCount[0];
  }

  get totalCount() {
    return this.appusersAndCount[1];
  }

  get getAppUsersSorted() {
    return (field, asc) => {
      if (!this.appusers) {
        return;
      } else if (this.sort.field) {
        return orderBy(
          this.appusers,
          [this.sort.field],
          [this.sort.asc ? 'asc' : 'desc']
        );
      } else {
        return this.appusers;
      }
    };
  }

  get getField() {
    return getField(this);
  }
}

/*export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};*/
